import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import MasterLayout from '../masterLayout/MasterLayout';
import Breadcrumb from '../components/Breadcrumb';
import api from '../services/api';
import { Switch } from 'antd';

const GeneralSettingsPage = () => {
  const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [initialData, setInitialData] = useState(null);

  // Fetch settings on component mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsData = await api.settings.getSettings();
        setInitialData(settingsData);
        
        // Map the settings to the form format
        const formData = {
          siteTitle: settingsData.site_title || '',
          adminEmail: settingsData.admin_email || '',
          itemsPerPage: settingsData.items_per_page?.toString() || '10',
          timezone: settingsData.timezone || 'UTC',
          maintenanceMode: settingsData.maintenance_mode === 'True' ? true : false,
          userRegistration: settingsData.allow_registration === 'True' ? true : false,
          emailNotifications: settingsData.enable_email_notifications === 'True' ? true : false
        };

        reset(formData);
      } catch (error) {
        console.error('Error fetching settings:', error);
        toast.error('Failed to load settings. Please refresh the page to try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, [reset]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const userId = user?.id ? parseInt(user.id) : 1;
      console.log('Submitting form with data:', { ...data, userId });
      
      // Prepare all settings in the format expected by the backend
      const settingsToUpdate = {
        site_title: data.siteTitle,
        admin_email: data.adminEmail,
        timezone: data.timezone,
        allow_registration: data.userRegistration,
        enable_email_notifications: data.emailNotifications,
        maintenance_mode: data.maintenanceMode
      };

      // Make a single API call to update all settings
      console.log('Submitting form with data:', settingsToUpdate);
      await api.settings.batchUpdateSettings(settingsToUpdate, userId);
      
      // If we get here, the update was successful
      toast.success('Settings saved successfully!');
      
      // Refresh the settings to ensure UI is in sync
      try {
        const response = await api.settings.getSettings();
        setInitialData(response.data);
      } catch (refreshError) {
        console.error('Error refreshing settings:', refreshError);
        // Non-critical error, just log it
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      // Show error message in a more user-friendly way
      toast.error(
        <div>
          <div className="fw-bold mb-2">Failed to save settings</div>
          <div style={{ whiteSpace: 'pre-line', textAlign: 'left' }}>{error.message}</div>
        </div>,
        { autoClose: 10000 }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <MasterLayout>
        <Breadcrumb title="General Settings" />
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2 mb-0">Loading settings...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </MasterLayout>
    );
  }

  return (
    <MasterLayout>
      <Breadcrumb title="General Settings" />
      
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title">System Settings</h4>
                <p className="card-title-desc">Configure general system settings</p>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Site Title</label>
                        <input
                          type="text"
                          className={`form-control ${errors.siteTitle ? 'is-invalid' : ''}`}
                          {...register('siteTitle', { required: 'Site title is required' })}
                          placeholder="Enter site title"
                        />
                        {errors.siteTitle && (
                          <div className="invalid-feedback">
                            {errors.siteTitle.message}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Admin Email</label>
                        <input
                          type="email"
                          className={`form-control ${errors.adminEmail ? 'is-invalid' : ''}`}
                          {...register('adminEmail', {
                            required: 'Admin email is required',
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: 'Invalid email address',
                            },
                          })}
                          placeholder="Enter admin email"
                        />
                        {errors.adminEmail && (
                          <div className="invalid-feedback">
                            {errors.adminEmail.message}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Items Per Page</label>
                        <select
                          className={`form-select ${errors.itemsPerPage ? 'is-invalid' : ''}`}
                          {...register('itemsPerPage', { required: 'Please select items per page' })}
                        >
                          <option value="">Select items per page</option>
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </select>
                        {errors.itemsPerPage && (
                          <div className="invalid-feedback">
                            {errors.itemsPerPage.message}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Timezone</label>
                        <select
                          className={`form-select ${errors.timezone ? 'is-invalid' : ''}`}
                          {...register('timezone', { required: 'Please select timezone' })}
                        >
                          <option value="">Select timezone</option>
                          <option value="UTC">UTC</option>
                          <option value="America/New_York">Eastern Time (ET)</option>
                          <option value="America/Chicago">Central Time (CT)</option>
                          <option value="America/Denver">Mountain Time (MT)</option>
                          <option value="America/Los_Angeles">Pacific Time (PT)</option>
                        </select>
                        {errors.timezone && (
                          <div className="invalid-feedback">
                            {errors.timezone.message}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-12">

                      <div className="form-check form-switch mb-3">
                        <Switch
                          id="maintenanceMode"
                          checked={!!watch('maintenanceMode')}
                          onChange={(checked) => setValue('maintenanceMode', checked, { shouldDirty: true })}
                          disabled={isSubmitting}
                          checkedChildren="On"
                          unCheckedChildren="Off"
                        />
                        <label className="form-check-label ms-2" htmlFor="maintenanceMode">
                          Maintenance Mode
                        </label>
                      </div>

                      <div className="form-check form-switch mb-3">
                        <Switch
                          id="userRegistration"
                          checked={!!watch('userRegistration')}
                          onChange={(checked) => setValue('userRegistration', checked, { shouldDirty: true })}
                          disabled={isSubmitting}
                          checkedChildren="On"
                          unCheckedChildren="Off"
                        />
                        <label className="form-check-label ms-2" htmlFor="userRegistration">
                          Allow New User Registration
                        </label>
                      </div>
                      
                      <div className="form-check form-switch mb-3">
                        <Switch
                          id="emailNotifications"
                          checked={!!watch('emailNotifications')}
                          onChange={(checked) => setValue('emailNotifications', checked, { shouldDirty: true })}
                          disabled={isSubmitting}
                          checkedChildren="On"
                          unCheckedChildren="Off"
                        />
                        <label className="form-check-label ms-2" htmlFor="emailNotifications">
                          Enable Email Notifications
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 d-flex justify-content-end">
                    <button
                      type="submit"
                      className="btn btn-primary me-3"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Saving...' : 'Save Settings'}
                    </button>
                    <button type="button" className="btn btn-light">
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MasterLayout>
  );
};

export default GeneralSettingsPage;
